import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import { withRouter } from '../../../utils/withRouter';
import { FormattedMessage, injectIntl } from 'react-intl';
import memoizeOne from 'memoize-one';

import { RootState } from '../../../reducers';
import * as _actionsJourney from '../../../actions/journey1';
import { Button, EButtonType, Input, BoxInput, IBoxInputAlignment, IBoxInputTypes } from '@repo/apg-ui';
import './Step1.scss';
import * as actions from '../../../actions/index';
import { testsFailed } from '../../../utils/validationTests';
import { EValidationTestType } from '../../../utils/validationConfigs';

import { ReactComponent as BreitesPublikum } from '../../../assets/icons/4_zielpublikum_breites_publikum.svg';
import { ReactComponent as Ausgewahltplatzen } from '../../../assets/icons/5_zielpublikum_ausgewahltplatzen.svg';
import { ReactComponent as IconInnovation } from '../../../assets/icons/e_i_innovation.svg';
import { useNavigate } from 'react-router';
import { PartiallyIntegratedUserDataEC } from '../../../reducers/app';

type InputTypes = 'address' | 'nr' | 'zipCode' | 'city';

type ErrorListTypes = { [key in InputTypes]: boolean };

interface Props {
  locales: any;
  intl: any;
  userDataEC: PartiallyIntegratedUserDataEC;
  actionsJourney: typeof _actionsJourney;
  history: any;
  validationMessageConfig: any;
  possibleBookingDates: any;
}

enum Audience {
  FIXED = 'fixed',
  FLEXIBLE = 'flexible',
}

const Step1 = (props: Props) => {
  const chooseDateTypeRadioOptions = [
    {
      title: props.intl.formatMessage({
        id: 'ec.journey01.step01.audience1label',
        defaultMessage: 'Breites Publikum ansprechen',
      }),
      text: props.intl.formatMessage({
        id: 'ec.journey01.step01.audience1explanation',
        defaultMessage: 'Um meine Marke oder mein Unternehmen bekannter und beliebter zu machen.',
      }),
      icon: <BreitesPublikum />,
      value: Audience.FIXED,
    },
    {
      title: props.intl.formatMessage({
        id: 'ec.journey01.step01.audience2label',
        defaultMessage: 'Gezielt Personen ansprechen',
      }),
      text: props.intl.formatMessage({
        id: 'ec.journey01.step01.audience2explanation',
        defaultMessage: 'Um den Verkauf meiner Produkte oder Dienstleistungen fokussiert zu fördern.',
      }),
      icon: <Ausgewahltplatzen />,
      value: Audience.FLEXIBLE,
    },
  ];

  const [stateData, setStateData] = React.useState({
    audience: props.userDataEC.audience,
    address: props.userDataEC.details.address,
    nr: props.userDataEC.details.nr,
    zipCode: props.userDataEC.details.zipCode,
    city: props.userDataEC.details.city,
  });

  const [errorListInputs, setErrorListInputs] = React.useState({
    address: false,
    nr: false,
    zipCode: false,
    city: false,
  });

  const navigate = useNavigate();

  const saveData = () => {
    const { address, zipCode, nr, city, audience } = stateData;
    props.actionsJourney.step1Save({
      audience,
      details: { address, nr, zipCode, city },
    });
  };

  const next = () => {
    saveData();

    if (stateData.audience === Audience.FIXED) {
      navigate({ pathname: '/journey1/2' });
    } else {
      checkFormByDefault().then((res) => {
        if (!res) {
          navigate({ pathname: '/journey1/2' });
        }
      });
    }
  };

  const checkFormByDefault = async () => {
    const inputs: InputTypes[] = ['address', 'nr', 'zipCode', 'city'];

    for (const input of inputs) {
      await validationInput(input)();
    }

    return memoizedIsDisableButton(stateData.audience, errorListInputs);
  };

  const validationInput =
    (inputName: InputTypes, validationTests: EValidationTestType[] = [EValidationTestType.hasValue]) =>
    () => {
      const value = stateData[inputName];
      let testsFailedArr: EValidationTestType[];

      testsFailedArr = testsFailed(validationTests, value);
      setErrorListInputs({
        ...errorListInputs,
        [inputName]: testsFailedArr.length > 0,
      });
    };

  const isDisableButton = (audience: any, errorListInputs: ErrorListTypes) =>
    audience === Audience.FIXED ? false : Object.values(errorListInputs).join('-').includes('true');

  const memoizedIsDisableButton = memoizeOne(isDisableButton);

  const isErrorClass = (inputName: InputTypes) => (errorListInputs[inputName] ? 'error-input' : '');

  return (
    <div className='step1'>
      <div>
        <div className='header'>
          <div className='d-flex'>
            <h4 className='title-journey'>
              <FormattedMessage
                id='ec.journey01.step01.header'
                defaultMessage='Welche Zielgruppen möchten Sie mit Ihrer Kampagne erreichen?'
              />
            </h4>
          </div>
        </div>

        <div className='mainStep'>
          <BoxInput
            id='date-input'
            options={chooseDateTypeRadioOptions}
            selected={stateData.audience}
            type={IBoxInputTypes.Radio}
            alignment={IBoxInputAlignment.FullWidth}
            onChange={(audience) => setStateData({ ...stateData, audience })}
          />

          {stateData.audience === Audience.FLEXIBLE && (
            <div className='mt-48 d-flex flex-wrap marginCompensation'>
              <div className='d-flex flex-column'>
                <p className='text-bold fw-bold ps-0 sub-title'>
                  <FormattedMessage
                    id='ec.journey01.step01.addressHeader'
                    defaultMessage='Bitte geben Sie eine Adresse ein, an welcher Sie Werbeflächen buchen wollen.'
                  />
                </p>

                <p className='text-bold sub-title d-flex align-items-center'>
                  <IconInnovation className='me-3' />
                  <FormattedMessage
                    id='ec.journey01.step01.additionalLocationsHelperText'
                    defaultMessage='In einem nächsten Schritt können Sie weitere Standorte eingeben.'
                  />
                </p>
              </div>

              <Input
                disabled={stateData.audience === null}
                value={stateData.address}
                name={'address'}
                onChange={(name, value) => setStateData({ ...stateData, address: value })}
                onBlur={validationInput('address')}
                type='text'
                placeholder={props.intl.formatMessage({
                  id: 'ec.forms.addressHint',
                  defaultMessage: 'Adresse eingeben',
                })}
                inputWrapperClass={`high col-16 ps-0 ${isErrorClass('address')}`}
              >
                {props.intl.formatMessage({
                  id: 'ec.forms.address',
                  defaultMessage: 'Adresse',
                })}
              </Input>

              <Input
                disabled={stateData.audience === null}
                value={stateData.nr}
                name={'nr'}
                onChange={(name, value) => setStateData({ ...stateData, nr: value })}
                onBlur={validationInput('nr')}
                type='text'
                placeholder={props.intl.formatMessage({
                  id: 'ec.forms.nrHint',
                  defaultMessage: 'Nr',
                })}
                inputWrapperClass={`high col-8 ps-4 ${isErrorClass('nr')}`}
              >
                {props.intl.formatMessage({
                  id: 'ec.forms.nr',
                  defaultMessage: 'Nr',
                })}
              </Input>

              <Input
                disabled={stateData.audience === null}
                value={stateData.zipCode}
                name={'zipcode'}
                onChange={(name, value) => setStateData({ ...stateData, zipCode: value })}
                onBlur={validationInput('zipCode')}
                type='text'
                placeholder={props.intl.formatMessage({
                  id: 'ec.forms.zipcodeHint',
                  defaultMessage: 'PLZ',
                })}
                inputWrapperClass={`high col-8 ps-0 ${isErrorClass('zipCode')}`}
              >
                {props.intl.formatMessage({
                  id: 'ec.forms.zipcode',
                  defaultMessage: 'PLZ',
                })}
              </Input>

              <Input
                disabled={stateData.audience === null}
                value={stateData.city}
                name={'city'}
                onChange={(name, value) => setStateData({ ...stateData, city: value })}
                onBlur={validationInput('city')}
                type='text'
                placeholder={props.intl.formatMessage({
                  id: 'ec.forms.ortHint',
                  defaultMessage: 'Ort',
                })}
                inputWrapperClass={`high col-16 ps-4 ${isErrorClass('city')}`}
              >
                {props.intl.formatMessage({
                  id: 'ec.forms.ort',
                  defaultMessage: 'Ort',
                })}
              </Input>
            </div>
          )}
        </div>

        <div className='d-flex twoButtons'>
          <Button
            isDisabled={memoizedIsDisableButton(stateData.audience, errorListInputs)}
            onClick={next}
            buttonType={EButtonType.PillMedium}
          >
            <FormattedMessage id='ec.components.buttons.next' defaultMessage='Weiter' />
          </Button>
        </div>
      </div>
    </div>
  );
};

function mapStateToProps(state: RootState, ownProps: any) {
  return {
    ...ownProps,
    ...state.app,
    ...state.router,
    locales: state.locales,
  };
}

function mapDispatchToProps(dispatch: Dispatch<actions.ACTION>) {
  return {
    actionsJourney: bindActionCreators(_actionsJourney as any, dispatch),
  };
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(injectIntl(Step1)));
